import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../../../environments/environment';

/**
 * Protects routes that require authentication (e.g., /panel).
 * Calls loadSessionToken() which fetches the Cognito session and stores
 * the access token in the service signal so the HTTP interceptor can use it.
 * Works for both email/password and Google OAuth sessions.
 */
export const authGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // OAuth popup returning from Cognito redirect (redirectSignIn → /panel/subscription).
  // Redirect to the spinner page so the user never sees the full panel UI.
  // The Hub 'signedIn' handler will sync the profile, save the session and close the popup.
  try {
    if (sessionStorage.getItem('eld_oauth_popup') === '1') {
      // Conservar code/state (y cualquier otro param) del callback de Cognito.
      // Si se pierden, Amplify no puede intercambiar el código y el popup se cierra
      // sin sesión en el primer registro OAuth.
      const qp: Record<string, string> = {};
      try {
        new URLSearchParams(window.location.search).forEach((v, k) => { qp[k] = v; });
      } catch { /* no-op */ }
      return router.createUrlTree(['/oauth-popup'], { queryParams: qp });
    }
  } catch { /* no-op */ }

  // Handoff token desde WordPress — solución al particionamiento de storage
  // cross-site (Chrome 115+ CHIPS / Safari ITP). WordPress pasa el access
  // token por URL (?_t=...) y Angular lo almacena en memoria y limpia la URL.
  const ht = route.queryParamMap.get('_t');
  if (ht) {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('_t');
      const q = u.searchParams.toString();
      history.replaceState(null, '', u.pathname + (q ? `?${q}` : '') + u.hash);
    } catch {
      history.replaceState(null, '', window.location.pathname);
    }
    const ok = await authService.restoreFromHandoffToken(ht);
    if (ok) return true;
  }

  const authenticated = await authService.loadSessionToken();
  if (authenticated) return true;

  // No hay sesión activa — redirigir a WordPress para que el usuario
  // inicie sesión desde allí (nunca directamente en /login).
  window.location.href = environment.wordpressUrl;
  return false;
};

/**
 * Prevents already-authenticated users from accessing guest pages (/login, /register).
 * Redirects them to the panel if a valid session exists.
 *
 * Exception: in embed mode (?embed=1) the guard always passes through so the
 * login/register component can detect the active session and post it to the
 * WordPress parent window via postMessage, instead of silently redirecting to
 * /panel inside the modal.
 */
/**
 * Redirects any unrecognised route to the WordPress site.
 * Used as a catch-all so users who land on an unknown URL are sent
 * to WordPress rather than seeing a blank/404 page on the Angular domain.
 */
export const wordpressRedirectGuard: CanActivateFn = () => {
  window.location.href = environment.wordpressUrl;
  return false;
};

export const noAuthGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const embed = route.queryParamMap.get('embed');

  // Se considera modo embed si:
  //  (a) la URL contiene ?embed=1 (primera carga del modal), o
  //  (b) la página ya corre dentro de un iframe (navegaciones internas como
  //      /login → /register → /forgot-password pierden el query param pero
  //      siguen ejecutándose dentro del mismo iframe del modal WordPress).
  const isEmbed = embed === '1' || embed === 'true' || window.self !== window.top;

  // /login, /register y /forgot-password son páginas internas SOLO para el
  // iframe embebido de WordPress. Si alguien accede directamente (sin iframe)
  // se le redirige al WordPress donde está el flujo de autenticación correcto.
  // En /login se agrega ?eld_login=1 para que events.js abra el modal de login
  // al cargar WordPress (enlaces de correos, ej. "Cuenta creada" → {link_login}).
  if (!isEmbed) {
    const base = environment.wordpressUrl.replace(/\/+$/, '');
    window.location.href = route.routeConfig?.path === 'login' ? `${base}/?eld_login=1` : base;
    return false;
  }

  // En modo embed: si ya tiene sesión activa, el componente la detecta y hace
  // postMessage al padre en lugar de mostrar el formulario.
  const authService = inject(AuthService);
  const authenticated = await authService.loadSessionToken();
  if (!authenticated) return true;

  // Usuario ya autenticado dentro del iframe — redirigir al panel.
  window.location.href = '/panel/subscription';
  return false;
};
